import type {
  MessageCampaign,
  MessageTranslation,
  ServiceNotice,
} from '@contract';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { CustomerFilter } from '@/components/audit/customer-filter';
import { PageHeader } from '@/components/page-header';
import { ProposeChange, useCanPropose } from '@/components/propose-change';
import { ReasonDialog } from '@/components/reason-dialog';
import { StatusBadge } from '@/components/record-table';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFrame,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePager, usePaging } from '@/components/ui/table-pager';
import { Textarea } from '@/components/ui/textarea';
import { callAdmin } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { languageName } from '@/lib/language';
import { adminQuery, invalidate } from '@/lib/query';
import { useCan } from '@/lib/staff-context';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/notices')({
  component: NoticesPage,
});

const ROUTE_PATTERN = /^\/\(main\)\/[A-Za-z0-9()/_?=&.-]{1,160}$/;

/** The top notice in the app, and messages to many customers. */
function NoticesPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const list = useQuery(
    adminQuery('notices.list', { include_archived: includeArchived }),
  );
  const paging = usePaging(list.data);
  const canPublish = useCanPropose('notice_publish');
  const canMessage = useCanPropose('message_segment');
  return (
    <>
      <PageHeader
        title="Notices"
        description="The service notice at the top of every customer's Home, and messages to many customers at once. Both are sent for approval."
      />
      {list.isError ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      <Checkbox
        checked={includeArchived}
        onCheckedChange={(value) => setIncludeArchived(value === true)}
      >
        Include archived
      </Checkbox>
      <TableFrame>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Notice</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Shows</TableHead>
              <TableHead>Published by</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground">
                  No notices yet.
                </TableCell>
              </TableRow>
            ) : null}
            {paging.visible?.map((notice) => (
              <TableRow key={notice.id}>
                <TableCell label="">
                  <div className="flex items-center gap-2 font-medium">
                    <StatusBadge status={notice.severity} />
                    {notice.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {notice.body}
                  </div>
                </TableCell>
                <TableCell label="State">
                  <StatusBadge status={notice.state} />
                </TableCell>
                <TableCell label="Shows" className="text-xs">
                  {formatDateTime(notice.starts_at)} to{' '}
                  {notice.ends_at ? formatDateTime(notice.ends_at) : 'no end'}
                </TableCell>
                <TableCell label="Published by" className="text-xs">
                  {notice.published_by}
                </TableCell>
                <TableCell label="">
                  {notice.state === 'ended' ? (
                    <ArchiveNotice notice={notice} />
                  ) : null}
                  {notice.state === 'live' || notice.state === 'scheduled' ? (
                    <EndNotice notice={notice} />
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePager paging={paging} />
      </TableFrame>
      <div className="grid gap-6 xl:grid-cols-2">
        {canPublish ? <ComposeNotice /> : null}
        {canMessage ? <MessageMany /> : null}
      </div>
      <MessagesSent />
      <Translations />
    </>
  );
}

/**
 * The server's customer messages in languages other than English: what a
 * customer who reads that language gets instead of the English in the code.
 * Read-only: a translation arrives with a native reviewer's sign-off, as a
 * migration, before that language's flag turns on.
 */
function Translations() {
  const catalogue = useQuery(adminQuery('messages.catalogue', {}));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Translations</CardTitle>
        <CardDescription>
          Every text Sao sends a customer (notices, pushes, texts, the sign-in
          code) is written in English. A customer who chose another language
          gets the translation listed here, or the English until one exists.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {catalogue.data && catalogue.data.length > 0 ? (
          <TableFrame>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Message</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Text</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catalogue.data.map((row) => (
                  <TranslationRow
                    key={`${row.code}:${row.language}`}
                    row={row}
                  />
                ))}
              </TableBody>
            </Table>
          </TableFrame>
        ) : (
          <p className="text-sm text-muted-foreground">
            {catalogue.isPending
              ? 'Loading…'
              : 'English only: every customer reads Sao in English until a market enables a second language.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function TranslationRow({ row }: Readonly<{ row: MessageTranslation }>) {
  return (
    <TableRow>
      <TableCell label="Message" className="font-mono text-xs">
        {row.code}
      </TableCell>
      <TableCell label="Language">{languageName(row.language)}</TableCell>
      <TableCell label="Text">
        {row.title ? <div className="font-medium">{row.title}</div> : null}
        <div>{row.body}</div>
        {row.sms ? (
          <div className="text-xs text-muted-foreground">SMS: {row.sms}</div>
        ) : null}
      </TableCell>
      <TableCell label="Updated" className="text-xs">
        {formatDateTime(row.updated_at)}
      </TableCell>
    </TableRow>
  );
}

/** Messages sent to many customers, with the super admin's way to cancel one. */
function MessagesSent() {
  const campaigns = useQuery(adminQuery('messages.campaigns', {}));
  if (!campaigns.data || campaigns.data.length === 0) return null;
  return (
    <TableFrame>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Message</TableHead>
            <TableHead>Sent</TableHead>
            <TableHead>Waiting</TableHead>
            <TableHead>Cancelled</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.data.map((campaign) => (
            <TableRow key={campaign.id}>
              <TableCell label="">
                <div className="font-medium">
                  {campaign.title ?? campaign.template}
                </div>
                <div className="text-xs text-muted-foreground">
                  {campaign.segment.replaceAll('_', ' ')} ·{' '}
                  {formatDateTime(campaign.applied_at)}
                </div>
              </TableCell>
              <TableCell label="Sent">{campaign.sent}</TableCell>
              <TableCell label="Waiting">{campaign.waiting}</TableCell>
              <TableCell label="Cancelled">{campaign.cancelled}</TableCell>
              <TableCell label="">
                <CancelMessage campaign={campaign} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

function CancelMessage({ campaign }: Readonly<{ campaign: MessageCampaign }>) {
  const [open, setOpen] = useState(false);
  const cancel = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('messages.cancel', { request_id: campaign.id, reason }),
    onSuccess: async (result) => {
      await invalidate('messages.campaigns');
      setOpen(false);
      toast.success(
        `Stopped ${result.stopped} waiting, withdrew ${result.withdrawn} from inboxes.`,
      );
    },
  });
  if (!useCan('messages.cancel')) return null;
  if (campaign.sent === 0 && campaign.waiting === 0) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Cancel
      </Button>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={`Cancel "${campaign.title ?? campaign.template}"`}
        description="Copies not yet delivered are stopped and the copy in each customer's inbox is withdrawn. A push or text already sent cannot be called back."
        confirmLabel="Cancel the message"
        pending={cancel.isPending}
        error={cancel.error?.message ?? null}
        onConfirm={(reason) => cancel.mutate(reason)}
      />
    </>
  );
}

function EndNotice({ notice }: Readonly<{ notice: ServiceNotice }>) {
  const [open, setOpen] = useState(false);
  const end = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('notices.end', { notice_id: notice.id, reason }),
    onSuccess: async () => {
      await invalidate('notices.list');
      setOpen(false);
      toast.success('The notice has ended.');
    },
  });
  if (!useCan('notices.end')) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        End now
      </Button>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={`End "${notice.title}"`}
        description="It leaves every app on the next refresh. Ending needs no approval; publishing again does."
        confirmLabel="End the notice"
        pending={end.isPending}
        error={end.error?.message ?? null}
        onConfirm={(reason) => end.mutate(reason)}
      />
    </>
  );
}

function ArchiveNotice({ notice }: Readonly<{ notice: ServiceNotice }>) {
  const [open, setOpen] = useState(false);
  const archive = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('notices.archive', { notice_id: notice.id, reason }),
    onSuccess: async () => {
      await invalidate('notices.list');
      setOpen(false);
      toast.success('The notice is archived.');
    },
  });
  if (!useCan('notices.archive')) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Archive
      </Button>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={`Archive "${notice.title}"`}
        description="It leaves this list. It is kept, and shows again under Include archived."
        confirmLabel="Archive the notice"
        pending={archive.isPending}
        error={archive.error?.message ?? null}
        onConfirm={(reason) => archive.mutate(reason)}
      />
    </>
  );
}

type Severity = ServiceNotice['severity'];

const SEVERITY_TONE: Record<Severity, string> = {
  info: 'border-border bg-info-tint',
  warning: 'border-border bg-warning-tint',
  critical: 'border-border bg-danger-tint',
};

function ComposeNotice() {
  const [severity, setSeverity] = useState<Severity>('info');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [actionLabel, setActionLabel] = useState('');
  const [actionRoute, setActionRoute] = useState('');
  const [dismissible, setDismissible] = useState(true);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const problems = [
    title.trim().length === 0 || title.trim().length > 80
      ? 'a title of 1 to 80 characters'
      : null,
    body.trim().length === 0 || body.trim().length > 280
      ? 'a body of 1 to 280 characters'
      : null,
    Boolean(actionLabel.trim()) !== Boolean(actionRoute.trim()) ||
    actionLabel.trim().length > 32 ||
    (actionRoute.trim() !== '' && !ROUTE_PATTERN.test(actionRoute.trim()))
      ? 'a button needs both a label (up to 32) and an in-app route such as /(main)/(wallet)'
      : null,
    !dismissible && severity !== 'critical'
      ? 'only a critical notice may stay up'
      : null,
  ].filter((problem): problem is string => problem !== null);
  const iso = (value: string) => (value ? new Date(value).toISOString() : '');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish a notice</CardTitle>
        <CardDescription>
          It shows at the top of Home from its start until its end, or until
          someone ends it.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Select
              label="Severity"
              value={severity}
              onChange={(event) => setSeverity(event.target.value as Severity)}
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </Select>
          </div>
          <div className="flex items-end gap-2 pb-2">
            <Checkbox
              checked={dismissible}
              onCheckedChange={(value) => setDismissible(value === true)}
            >
              Customers may put it away
            </Checkbox>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="notice-title">Title</Label>
          <Input
            id="notice-title"
            maxLength={80}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="notice-body">Body</Label>
          <Textarea
            id="notice-body"
            maxLength={280}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="notice-action-label">Button label (optional)</Label>
            <Input
              id="notice-action-label"
              maxLength={32}
              value={actionLabel}
              onChange={(event) => setActionLabel(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="notice-action-route">Button route</Label>
            <Input
              id="notice-action-route"
              placeholder="/(main)/(wallet)"
              value={actionRoute}
              onChange={(event) => setActionRoute(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <DatePicker
              label="Starts (empty: on approval)"
              granularity="minute"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <DatePicker
              label="Ends (empty: until ended)"
              granularity="minute"
              value={endsAt}
              onChange={(event) => setEndsAt(event.target.value)}
            />
          </div>
        </div>
        <div
          className={cn(
            'flex flex-col gap-1 rounded-lg border p-3',
            SEVERITY_TONE[severity],
          )}
        >
          <span className="text-xs text-muted-foreground">Preview</span>
          <span className="font-medium">{title || 'Title'}</span>
          <span className="text-sm">{body || 'What customers read.'}</span>
          {actionLabel ? (
            <span className="text-sm font-medium underline">{actionLabel}</span>
          ) : null}
        </div>
        {problems.length > 0 && (title || body) ? (
          <Alert variant="destructive">Needs {problems.join('; ')}.</Alert>
        ) : null}
        {problems.length === 0 ? (
          <div>
            <ProposeChange
              kind="notice_publish"
              variant="default"
              payload={{
                severity,
                title: title.trim(),
                body: body.trim(),
                action_label: actionLabel.trim(),
                action_route: actionRoute.trim(),
                dismissible,
                starts_at: iso(startsAt),
                ends_at: iso(endsAt),
              }}
              label="Send the notice for approval"
              title={`Publish "${title.trim()}"`}
              description={`A ${severity} notice for every customer.`}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

type Audience = 'everyone' | 'limit_group' | 'customer';

function MessageMany() {
  const templates = useQuery(adminQuery('messages.templates', {}));
  const canListGroups = useCan('limits.list');
  const groups = useQuery({
    ...adminQuery('limits.list', {}),
    enabled: canListGroups,
  });
  const segmentTemplates = templates.data?.filter((row) => row.allow_segment);
  const [code, setCode] = useState('');
  const [segment, setSegment] = useState<Audience>('everyone');
  const [group, setGroup] = useState('');
  const [customer, setCustomer] = useState<string | undefined>();
  const template = segmentTemplates?.find(
    (row) => row.code === (code || segmentTemplates[0]?.code),
  );
  const ready =
    segment === 'everyone' ||
    (segment === 'limit_group' && group.trim() !== '') ||
    (segment === 'customer' && customer !== undefined);
  const count = useQuery({
    ...adminQuery('messages.segment_count', {
      segment,
      limit_group: segment === 'limit_group' ? group.trim() : undefined,
      customer_id: segment === 'customer' ? customer : undefined,
    }),
    enabled: ready,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Message many</CardTitle>
        <CardDescription>
          A template to everyone, to one limit group or to one customer: in the
          app and by push, never by SMS.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Select
            label="Template"
            value={template?.code ?? ''}
            onChange={(event) => setCode(event.target.value)}
          >
            {segmentTemplates?.map((row) => (
              <option key={row.code} value={row.code}>
                {row.name}
              </option>
            ))}
          </Select>
        </div>
        <TemplatePreview template={template} />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Select
              label="To"
              value={segment}
              onChange={(event) => setSegment(event.target.value as Audience)}
            >
              <option value="everyone">Everyone</option>
              <option value="limit_group">One limit group</option>
              <option value="customer">One customer</option>
            </Select>
          </div>
          {segment === 'limit_group' ? (
            <div className="flex flex-col gap-1">
              {groups.data ? null : (
                <Label htmlFor="many-group">Limit group</Label>
              )}
              {groups.data ? (
                <Select
                  label="Limit group"
                  value={group}
                  onChange={(event) => setGroup(event.target.value)}
                >
                  <option value="">Pick a group</option>
                  {groups.data.groups.map((row) => (
                    <option key={row.code} value={row.code}>
                      {row.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  id="many-group"
                  placeholder="Group code"
                  value={group}
                  onChange={(event) => setGroup(event.target.value)}
                />
              )}
            </div>
          ) : null}
          {segment === 'customer' ? (
            <CustomerFilter
              label="Customer"
              value={customer}
              onChange={setCustomer}
            />
          ) : null}
        </div>
        {count.data ? (
          <p className="text-sm">
            Goes to <strong>{count.data.count}</strong> customer
            {count.data.count === 1 ? '' : 's'}.
          </p>
        ) : null}
        {template && ready ? (
          <div>
            <ProposeChange
              kind="message_segment"
              variant="default"
              payload={{
                template: template.code,
                segment,
                limit_group: segment === 'limit_group' ? group.trim() : null,
                customer_id: segment === 'customer' ? customer : null,
              }}
              label="Send the message for approval"
              title={`Send "${template.name}"`}
              description={`To ${count.data?.count ?? 'the'} customer${count.data?.count === 1 ? '' : 's'}.`}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function TemplatePreview({
  template,
}: Readonly<{ template?: { title: string; body: string } }>) {
  if (!template) return null;
  return (
    <div className="rounded-lg border bg-surface-2/40 p-3 text-sm">
      <p className="font-medium">{template.title}</p>
      <p className="text-muted-foreground">{template.body}</p>
    </div>
  );
}
